import { memo, useCallback, useState } from "react";
import { useBoardContext } from "../../contexts/BoardContext";
import { nodeDataIsUnchanged } from "../../utils/render";
import { StepDefinition } from "../../stepDefinitions";
import SettingsPopup from "../ui/SettingsPopup";
import Tag from "../ui/Tag";
import AbstractNode from "../AbstractNode";

type JoinArrayFilterData = { separator: string };
type JoinArrayFilterInputs = { inputValues: string[] };
type JoinArrayPreset = {
  key: string;
  label: string;
  char: string;
};

const JoinArrayFilter = memo((props) => {
  const { updateElementData } = useBoardContext();
  const [separatorValue, setSeparatorValue] = useState(props.data.separator);

  const commit = useCallback(() => {
    updateElementData(props.id, { separator: separatorValue });
  }, [updateElementData, props.id, separatorValue]);

  return (
    <AbstractNode
      buttons={
        <SettingsPopup onSubmit={commit}>
          <div className="space-y-4">
            <label className="block">
              <span className="text-sm font-semibold text-neutral-900">
                Separator
              </span>

              <input
                className="input input--neutral"
                value={separatorValue}
                onChange={({ target }) => setSeparatorValue(target.value)}
              />

              <div className="flex flex-wrap gap-1 mt-2">
                {presets.map((preset) => (
                  <Tag
                    as="button"
                    key={preset.key}
                    onClick={() => setSeparatorValue(preset.char)}
                  >
                    {preset.label}
                  </Tag>
                ))}
              </div>
            </label>
          </div>
        </SettingsPopup>
      }
      {...props}
    />
  );
}, nodeDataIsUnchanged);

export default JoinArrayFilter;

const presets: JoinArrayPreset[] = [
  {
    key: "line",
    label: "Line Break",
    char: "\\n",
  },
  {
    key: "comma",
    label: "Comma",
    char: ",",
  },
  {
    key: "space",
    label: "Space",
    char: " ",
  },
  {
    key: "character",
    label: "Character",
    char: "",
  },
];

function replaceSpecialTokens(separator: string): string {
  return separator.replace("\\n", "\n").replace("\\t", "\t");
}

export const definition: StepDefinition<
  JoinArrayFilterData,
  JoinArrayFilterInputs
> = {
  label: "Join Array",
  description: "Joins an array into a string.",
  group: "filter",

  component: JoinArrayFilter,

  pipe({ inputValues }, { separator }) {
    return inputValues?.length && inputValues.join(replaceSpecialTokens(separator));
  },

  init(data) {
    data.separator ??= ",";
  },

  refresh(data) {},

  inputs: {
    inputValues: {
      label: "Input Array",
      type: "StringArray",
      required: true,
      acceptMultiple: false,
    },
  },
  outputs: {
    joinedString: {
      label: "Joined String",
      type: "String",
    },
  },
};
